import { ArrowRight, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function JourneySections() {
  const navigate = useNavigate();
  const journeys = [
    {
      id: "credit-score-eligibility",
      title: "Discover your best loan offer",
      description:
        "See your eligible loan amount, rate, and EMI. Personalized to your credit profile with no impact on your credit score.",
      steps: [
        "Check your credit score for free",
        "See your personalized loan eligibility",
        "View pre-approved rates from multiple lenders",
      ],
      ctaText: "Check My Eligibility",
      color: "bg-gradient-to-r from-[#59266D]/90 to-[#59266D]",
      iconBg: "bg-[#59266D]/10",
      url: "/trackloanstatus",
    },
    {
      id: "emi-savings",
      title: "Reduce your EMIs & get top-up offers on running loans",
      description:
        "Use our algorithm to reduce your EMI based on your credit score and the best rates offered by top banks.",
      steps: [
        "Check credit score",
        "Confirm existing EMIs",
        "Get EMI saving & top-up offers",
      ],
      ctaText: "Reduce my EMIs",
      color: "bg-gradient-to-r from-[#3a868b]/90 to-[#3a868b]",
      iconBg: "bg-[#3a868b]/10",
      url: "/lower-emi-page",
    },
    {
      id: "ai-recommendation",
      title: "AI-Driven Loan Recommendations",
      description:
        "Let our AI engine find the most suited loan options from 100+ products in seconds.",
      steps: [
        "Answer a few simple questions",
        "Fetch your credit score",
        "Get your best loan matches",
      ],
      ctaText: "Get loan recommendations",
      color: "bg-gradient-to-r from-[#59266D]/80 to-[#59266D]",
      iconBg: "bg-[#59266D]/10",
      url: "/loan-recommendation",
    },
  ];

  return (
    <section className="bg-white py-16 md:py-20" id="how-it-works">
      <div className="container px-3 md:px-6">
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold mb-3">
            How MinEMI Works For You
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto font-medium tracking-wide">
            See personalized loan and EMI options using your credit score and
            get the best deals from banks
          </p>
          <div className="mt-4 mx-auto w-20 h-1 bg-[#59266D] rounded-full"></div>
        </div>

        <div className="max-w-5xl mx-auto">
          {journeys.map((journey, index) => (
            <div key={journey.id} className="mb-8 md:mb-12 last:mb-0">
              {/* Content card with integrated journey number */}
              <div className="bg-[#f9f9fc] rounded-2xl p-6 md:p-8 relative">
                {/* Integrated journey number */}
                <div className="bg-[#59266D] text-white w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-lg absolute top-0 left-0 transform -translate-x-3 -translate-y-3 border-4 border-white">
                  {index + 1}
                </div>

                <div className="md:flex md:items-start md:gap-8">
                  {/* Left column - Title and description */}
                  <div className="md:w-1/2 mb-6 md:mb-0">
                    <h3 className="text-[1.29rem] leading-8 font-bold mb-2">
                      {journey.title}
                    </h3>
                    <p className="text-gray-600 text-[1.02rem] font-medium tracking-wide text-start mb-3">
                      {journey.description}
                    </p>

                    <button
                      type="button"
                      onClick={() => {
                        if (journey.url) {
                          navigate(journey.url);
                        }
                      }}
                      className="w-full md:w-auto rounded-full bg-[#59266D] hover:bg-[#59266D]/90 text-white py-2 px-4 font-medium flex items-center justify-center transition-colors duration-200"
                    >
                      {journey.ctaText}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                  </div>

                  {/* Right column - Steps */}
                  <div className="md:w-1/2 bg-[#59266D]/10 rounded-xl p-3 sm:p-4">
                    <h4 className="font-medium text-lg mb-3">Simple Steps:</h4>
                    <div className="space-y-3">
                      {journey.steps.map((step, stepIndex) => (
                        <div
                          key={stepIndex}
                          className="flex items-start tracking-wide"
                        >
                          <CheckCircle className="h-5 w-5 text-[#59266D] mr-2 flex-shrink-0 mt-0.5" />
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Connector line (except for last item) */}
              {index < journeys.length - 1 && (
                <div className="h-8 w-0.5 bg-gray-200 mx-auto my-0"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
